import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Check, X, Trash2, Shield, User, Star, ShieldCheck, ShieldOff, CloudDownload, Plus, Minus, Calendar, CalendarX, Tv2 } from 'lucide-react';
import { FEATURE_TIKTOK } from '../config/features';
import AdminEsp32 from './AdminEsp32';
import AdminFirmware from './AdminFirmware';
import AdminDiagrama from './AdminDiagrama';

const AdminPanel = ({ db, currentUserId, onClose }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usosInput, setUsosInput] = useState({});
  const [fechaInput, setFechaInput] = useState({});

  useEffect(() => {
    cargarUsuarios();
  }, []);

  useEffect(() => {
    const initUsos = {};
    const initFecha = {};
    usuarios.forEach(u => {
      initUsos[u.id] = String(u.stringArtProUsos ?? 0);
      const ts = u.stringArtProExpira;
      initFecha[u.id] = ts?.toDate ? ts.toDate().toISOString().split('T')[0] : '';
    });
    setUsosInput(initUsos);
    setFechaInput(initFecha);
  }, [usuarios]);

  const cargarUsuarios = async () => {
    try {
      const q = query(collection(db, "users"));
      const snapshot = await getDocs(q);
      const lista = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsuarios(lista);
    } catch (error) {
      console.error("Error cargando usuarios", error);
    } finally {
      setLoading(false);
    }
  };

  const guardarUsos = async (id) => {
    const valor = parseInt(usosInput[id] ?? '0', 10);
    if (isNaN(valor) || valor < 0) return;
    const fechaStr = fechaInput[id];
    const expira = fechaStr ? new Date(fechaStr + 'T23:59:59') : null;
    try {
      await updateDoc(doc(db, "users", id), {
        stringArtProUsos: valor,
        stringArtProExpira: expira,
      });
      cargarUsuarios();
    } catch (error) {
      console.error("Error guardando usos String Art Pro", error);
    }
  };

  const aprobarUsuario = async (id) => {
    try {
      await updateDoc(doc(db, "users", id), { estado: 'aprobado' });
      cargarUsuarios(); // Recargar lista
    } catch (error) {
      console.error("Error al aprobar", error);
    }
  };

  const rechazarUsuario = async (id) => {
    if (window.confirm("¿Estás seguro de rechazar y eliminar este usuario?")) {
      try {
        await deleteDoc(doc(db, "users", id));
        cargarUsuarios();
      } catch (error) {
        console.error("Error al eliminar", error);
      }
    }
  };

  const toggleRol = async (id, currentRol) => {
    const nuevoRol = currentRol === 'pro' ? 'standard' : 'pro';
    try {
      await updateDoc(doc(db, "users", id), { rol: nuevoRol });
      cargarUsuarios();
    } catch (error) {
      console.error("Error cambiando rol", error);
    }
  };

  const toggleTikTok = async (id, currentCanUseTikTok) => {
    try {
      await updateDoc(doc(db, "users", id), { canUseTikTok: !currentCanUseTikTok });
      cargarUsuarios();
    } catch (error) {
      console.error("Error cambiando canUseTikTok", error);
    }
  };

  const toggleAdmin = async (id, currentIsAdmin) => {
    if (id === currentUserId) {
      alert("No puedes quitarte el acceso de administrador a ti mismo.");
      return;
    }
    const accion = currentIsAdmin ? "quitar los permisos de administrador a" : "hacer administrador a";
    if (!window.confirm(`¿Estás seguro de ${accion} este usuario?`)) return;
    try {
      await updateDoc(doc(db, "users", id), { isAdmin: !currentIsAdmin });
      cargarUsuarios();
    } catch (error) {
      console.error("Error cambiando isAdmin", error);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-slate-900/95 backdrop-blur flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-slate-800 w-full max-w-4xl h-[80vh] rounded-2xl border border-slate-700 flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-900/50">
          <div className="flex items-center gap-3">
            <Shield className="text-blue-500" size={24} />
            <h2 className="text-xl font-bold text-white">Panel de Administración</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-auto p-6 custom-scrollbar">
          <AdminFirmware />
          <AdminDiagrama />
          <AdminEsp32 db={db} />
          {loading ? (
             <div className="flex justify-center p-8">
               <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
             </div>
          ) : (
            <div className="grid gap-4">
              {usuarios.map(u => (
                <div key={u.id} className="bg-slate-700/30 rounded-xl border border-slate-600 hover:bg-slate-700/50 transition-colors overflow-hidden">
                  {/* Fila principal */}
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${
                        u.estado === 'aprobado' 
                          ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                          : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 animate-pulse'
                      }`}>
                        <User size={20} />
                      </div>
                      <div>
                        <div className="text-white font-bold text-lg flex items-center gap-2 flex-wrap">
                          {u.email || 'Sin Email'}
                          {u.rol === 'pro' && (
                            <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-2 py-0.5 rounded border border-yellow-500/30 font-black uppercase tracking-wider flex items-center gap-1">
                              <Star size={10} fill="currentColor" /> PRO
                            </span>
                          )}
                          {u.isAdmin && (
                            <span className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded border border-blue-500/30 font-black uppercase tracking-wider flex items-center gap-1">
                              <Shield size={10} /> ADMIN
                            </span>
                          )}
                          {FEATURE_TIKTOK && u.canUseTikTok && (
                            <span className="bg-pink-500/20 text-pink-400 text-[10px] px-2 py-0.5 rounded border border-pink-500/30 font-black uppercase tracking-wider flex items-center gap-1">
                              <Tv2 size={10} /> TIKTOK
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 font-mono">ID: {u.id}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Registrado: {u.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    {/* Botón TikTok */}
                    {FEATURE_TIKTOK && (
                    <button
                      onClick={() => toggleTikTok(u.id, u.canUseTikTok)}
                      title={u.canUseTikTok ? 'Quitar acceso TikTok' : 'Dar acceso TikTok'}
                      className={`p-2 rounded-lg border transition-colors ${
                        u.canUseTikTok
                          ? 'bg-pink-500/10 text-pink-400 border-pink-500/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30'
                          : 'bg-slate-600/30 text-slate-400 border-slate-600 hover:bg-pink-500/10 hover:text-pink-400 hover:border-pink-500/30'
                      }`}
                    >
                      <Tv2 size={16} />
                    </button>
                    )}

                    {/* Botón de Admin */}
                    <button
                      onClick={() => toggleAdmin(u.id, u.isAdmin)}
                      disabled={u.id === currentUserId}
                      title={u.id === currentUserId ? 'No puedes modificar tu propio rol de admin' : (u.isAdmin ? 'Quitar Admin' : 'Hacer Admin')}
                      className={`p-2 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        u.isAdmin
                          ? 'bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/30'
                          : 'bg-slate-600/30 text-slate-400 border-slate-600 hover:bg-blue-500/10 hover:text-blue-400 hover:border-blue-500/30'
                      }`}
                    >
                      {u.isAdmin ? <ShieldOff size={16} /> : <ShieldCheck size={16} />}
                    </button>

                    {/* Botón de Rol */}
                    <button
                      onClick={() => toggleRol(u.id, u.rol)}
                      className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border transition-colors ${
                        u.rol === 'pro'
                          ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/20'
                          : 'bg-slate-600/30 text-slate-400 border-slate-600 hover:bg-slate-600/50'
                      }`}
                    >
                      {u.rol === 'pro' ? 'Bajar a Standard' : 'Hacer PRO'}
                    </button>

                    {u.estado !== 'aprobado' ? (
                      <button 
                        onClick={() => aprobarUsuario(u.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-all font-bold shadow-lg shadow-green-900/20"
                        title="Aprobar acceso"
                      >
                        <Check size={18} />
                        Aprobar
                      </button>
                    ) : (
                      <span className="px-4 py-1 bg-green-500/10 text-green-500 text-xs font-bold rounded-full border border-green-500/20 uppercase tracking-widest">
                        Aprobado
                      </span>
                    )}
                    
                    <button 
                      onClick={() => rechazarUsuario(u.id)}
                      className="p-2 bg-slate-700 text-red-400 hover:bg-red-500/20 hover:text-red-300 rounded-lg transition-colors border border-transparent hover:border-red-500/30"
                      title="Eliminar usuario"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                  </div>

                  {/* Fila de usos String Art Pro (solo usuarios PRO) */}
                  {u.rol === 'pro' && (() => {
                    const expiraDate = u.stringArtProExpira?.toDate?.();
                    const estaExpirado = expiraDate ? new Date() > expiraDate : false;
                    const diasRestantes = expiraDate
                      ? Math.ceil((expiraDate - new Date()) / (1000 * 60 * 60 * 24))
                      : null;
                    return (
                      <div className="px-4 pb-4 pt-0 border-t border-slate-600/40">
                        {/* Cabecera de sección */}
                        <div className="mt-3 flex items-center gap-2 mb-3">
                          <CloudDownload size={13} className="text-indigo-400 shrink-0" />
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">String Art Pro</span>
                          {/* Estado actual */}
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded border ml-1 ${
                            estaExpirado
                              ? 'bg-red-500/10 text-red-400 border-red-500/20'
                              : (u.stringArtProUsos ?? 0) > 0 && expiraDate
                                ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                : 'bg-slate-600/20 text-slate-500 border-slate-600'
                          }`}>
                            {estaExpirado
                              ? '⚠ EXPIRADO'
                              : (u.stringArtProUsos ?? 0) > 0 && expiraDate
                                ? `✓ ACTIVO · ${diasRestantes}d`
                                : 'SIN LICENCIA'}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-3 items-end">
                          {/* Control de usos */}
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] text-slate-500 uppercase tracking-widest">Usos</span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setUsosInput(prev => ({ ...prev, [u.id]: String(Math.max(0, parseInt(prev[u.id] ?? '0', 10) - 1)) }))}
                                className="w-6 h-6 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-slate-300 rounded border border-slate-600 transition-colors"
                              >
                                <Minus size={11} />
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={usosInput[u.id] ?? '0'}
                                onChange={(e) => setUsosInput(prev => ({ ...prev, [u.id]: e.target.value }))}
                                className="w-14 px-1 py-1 bg-slate-900 border border-slate-600 rounded text-white text-xs font-mono text-center focus:border-indigo-500 focus:outline-none"
                              />
                              <button
                                onClick={() => setUsosInput(prev => ({ ...prev, [u.id]: String(parseInt(prev[u.id] ?? '0', 10) + 1) }))}
                                className="w-6 h-6 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-slate-300 rounded border border-slate-600 transition-colors"
                              >
                                <Plus size={11} />
                              </button>
                              <span className="text-[10px] text-slate-500 ml-1 font-mono">
                                actual: {u.stringArtProUsos ?? 0}
                              </span>
                            </div>
                          </div>

                          {/* Control de fecha */}
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-1">
                              {estaExpirado ? <CalendarX size={10} className="text-red-400" /> : <Calendar size={10} />}
                              Vence el
                            </span>
                            <input
                              type="date"
                              value={fechaInput[u.id] ?? ''}
                              min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => setFechaInput(prev => ({ ...prev, [u.id]: e.target.value }))}
                              className={`px-2 py-1 bg-slate-900 border rounded text-white text-xs font-mono focus:outline-none transition-colors ${
                                estaExpirado
                                  ? 'border-red-500/50 focus:border-red-400'
                                  : 'border-slate-600 focus:border-indigo-500'
                              }`}
                            />
                            {expiraDate && (
                              <span className={`text-[10px] font-mono ${estaExpirado ? 'text-red-400' : 'text-slate-500'}`}>
                                {estaExpirado ? 'Venció: ' : 'Vence: '}
                                {expiraDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                          </div>

                          {/* Botón asignar */}
                          <button
                            onClick={() => guardarUsos(u.id)}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-colors shadow-lg shadow-indigo-900/20 flex items-center gap-1.5 self-end"
                          >
                            <Check size={13} /> Asignar
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
              {usuarios.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <User size={48} className="mx-auto mb-4 opacity-20" />
                  <p>No hay usuarios registrados en el sistema.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;

